import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { MarketingLayout } from './layouts/MarketingLayout';
import { HomePage } from './pages/HomePage';
import { FeaturesPage } from './pages/FeaturesPage';
import { WhyRooflyzerPage } from './pages/WhyRooflyzerPage';
import { ForRoofersPage } from './pages/ForRoofersPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { MarketingDashboardPage } from './pages/MarketingDashboardPage';
import { EstimatorPage } from './pages/EstimatorPage';
import { ContractorsPage } from './pages/ContractorsPage';
import { ContractorDirectoryPage } from './pages/contractors/ContractorDirectoryPage';
import { ContractorProfilePage } from './pages/contractors/ContractorProfilePage';
import { ChangelogPage } from './pages/ChangelogPage';
import { ComparePage } from './pages/ComparePage';
import { CompareDetailPage } from './pages/CompareDetailPage';
import { LeadNurturePage } from './pages/LeadNurturePage';
import { AgenciesPage } from './pages/AgenciesPage';
import { FAQPage } from './pages/FAQPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { SecurityPage } from './pages/SecurityPage';
import { DataFreedomPledgePage } from './pages/DataFreedomPledgePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { DataDeletionPage } from './pages/DataDeletionPage';
import { DataDeletionStatusPage } from './pages/DataDeletionStatusPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { EulaPage } from './pages/EulaPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { CaseStudiesPage } from './pages/customers/CaseStudiesPage';
import { HappyClientsPage } from './pages/customers/HappyClientsPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { RobotsTxtPage } from './pages/RobotsTxtPage';
import { SitemapPage } from './pages/SitemapPage';
import { SitemapXmlPage } from './pages/SitemapXmlPage';
import { CMSAdmin } from './pages/admin/CMSAdmin';
import { LeadTrackingFeaturePage } from './pages/features/LeadTrackingPage';
import { CanvassingWidgetPage } from './pages/features/CanvassingWidgetPage';
import { MarketingDashboardFeaturePage } from './pages/features/MarketingDashboardFeaturePage';
import { SeedCatalogsPage } from './pages/features/SeedCatalogsPage';
import { AIReadyCRMPage } from './pages/features/AIReadyCRMPage';
import { InvoicingPage } from './pages/features/InvoicingPage';
import { ProposalsPage } from './pages/features/ProposalsPage';
import { ProjectManagementPage } from './pages/features/ProjectManagementPage';
import { StormCanvassingPage } from './pages/features/StormCanvassingPage';
import { SchedulingPage } from './pages/features/SchedulingPage';
import { CRMPage } from './pages/features/CRMPage';
import { EstimatingPage } from './pages/features/EstimatingPage';
import { MarketingAutomationPage } from './pages/features/MarketingAutomationPage';
import { DemoPage } from './pages/DemoPage';
import { OurStoryPage } from './pages/OurStoryPage';
import { FoundersBetaPage } from './pages/community/FoundersBetaPage';
import { PricingPage } from './pages/PricingPage';
// Eager like every other route: a lazy chunk with no error boundary blanked the
// whole site when the chunk failed to load (review finding on #2612).
import { EnterprisePage } from './pages/EnterprisePage';

/** Scroll to the top whenever the route changes. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Redirect legacy brand URLs to the matching contractor profile. */
function BrandSlugRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/contractors/${slug}`} replace />;
}

/** Shared route table used by both client BrowserRouter and SSR prerenderer. */
export function AppRoutes() {
  return (
    <Routes>
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/features/lead-tracking" element={<LeadTrackingFeaturePage />} />
        <Route path="/features/canvassing-widget" element={<CanvassingWidgetPage />} />
        <Route path="/features/marketing-dashboard" element={<MarketingDashboardFeaturePage />} />
        <Route path="/features/seed-catalogs-templates" element={<SeedCatalogsPage />} />
        <Route path="/features/ai-roofing-crm" element={<AIReadyCRMPage />} />
        <Route path="/features/invoicing" element={<InvoicingPage />} />
        <Route path="/features/proposals" element={<ProposalsPage />} />
        <Route path="/features/project-management" element={<ProjectManagementPage />} />
        <Route path="/features/storm-canvassing" element={<StormCanvassingPage />} />
        <Route path="/features/scheduling" element={<SchedulingPage />} />
        <Route path="/features/crm" element={<CRMPage />} />
        <Route path="/features/estimating" element={<EstimatingPage />} />
        <Route path="/features/marketing-automation" element={<MarketingAutomationPage />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/why-rooflyzer" element={<WhyRooflyzerPage />} />
        <Route path="/for-roofers" element={<ForRoofersPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/marketing-dashboard" element={<MarketingDashboardPage />} />
        <Route path="/marketing" element={<MarketingDashboardPage />} />
        <Route path="/estimator" element={<EstimatorPage />} />
        <Route path="/for-contractors" element={<ContractorsPage />} />
        <Route path="/contractors" element={<ContractorDirectoryPage />} />
        <Route path="/contractors/:slug" element={<ContractorProfilePage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/compare/:slug" element={<CompareDetailPage />} />
        <Route path="/nurture" element={<LeadNurturePage />} />
        <Route path="/agencies" element={<AgenciesPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/our-story" element={<OurStoryPage />} />
        <Route path="/customers/case-studies" element={<CaseStudiesPage />} />
        <Route path="/customers/happy-clients" element={<HappyClientsPage />} />
        <Route
          path="/customers/wall-of-love"
          element={<Navigate to="/customers/happy-clients" replace />}
        />
        <Route path="/our-story/agency-to-crm" element={<Navigate to="/our-story" replace />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/enterprise" element={<EnterprisePage />} />
        <Route path="/data-freedom" element={<DataFreedomPledgePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/brand" element={<Navigate to="/contractors" replace />} />
        <Route path="/brand/:slug" element={<BrandSlugRedirect />} />
        <Route path="/roofing-contractors/:slug" element={<BrandSlugRedirect />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/community/founders-beta-program" element={<FoundersBetaPage />} />
        <Route path="/beta" element={<FoundersBetaPage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        <Route path="/data-deletion/status" element={<DataDeletionStatusPage />} />
        <Route path="/data-deletion/status/:code" element={<DataDeletionStatusPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/eula" element={<EulaPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="/admin" element={<CMSAdmin />} />
      <Route path="/robots.txt" element={<RobotsTxtPage />} />
      <Route path="/sitemap.xml" element={<SitemapXmlPage />} />
    </Routes>
  );
}

/** Mount the marketing routes and their shared layout. */
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;
